import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import axios from "axios";
import { API } from "@/config";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Bell, Mail, MessageCircle, Calendar, FileText, AlertCircle, X } from "lucide-react";
import { toast } from "sonner";

/**
 * NotificationSettings - Reusable component for user/clinic notification preferences
 * @param {string} token - Auth token
 * @param {string} type - "user" or "clinic"
 * @param {string} clinicId - Required if type is "clinic"
 * @param {object} initialSettings - Initial settings data (optional)
 * @param {function} onUpdate - Callback when settings change
 */
export default function NotificationSettings({ token, type = "user", clinicId, initialSettings, onUpdate }) {
    const [settings, setSettings] = useState({
        enabled: false,
        channels: { whatsapp: true, email: true },
        events: {
            appointment_created: true,
            appointment_reminder: true,
            appointment_cancelled: true,
            invoice_generated: true,
            invoice_cancelled: true
        },
        reminder_hours: 6
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Fetch current settings
    useEffect(() => {
        if (initialSettings) {
            setSettings(prev => ({ ...prev, ...initialSettings }));
            setLoading(false);
            return;
        }

        const fetchSettings = async () => {
            try {
                let url = `${API}/notifications/settings`;
                if (type === "clinic" && clinicId) {
                    url = `${API}/clinics/${clinicId}/notification-settings`;
                }

                const res = await axios.get(url, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data) {
                    setSettings(prev => ({ ...prev, ...res.data }));
                }
            } catch {
                console.log("No notification settings found, using defaults");
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, [token, type, clinicId, initialSettings]);

    // Save settings
    const saveSettings = async (newSettings) => {
        setSaving(true);
        try {
            let url = `${API}/notifications/settings`;
            if (type === "clinic" && clinicId) {
                url = `${API}/clinics/${clinicId}/notification-settings`;
            }

            await axios.put(url, newSettings, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setSettings(newSettings);
            onUpdate?.(newSettings);
            toast.success("Notification settings saved");
        } catch (error) {
            console.error("Failed to save settings:", error);
            toast.error("Failed to save notification settings");
        } finally {
            setSaving(false);
        }
    };

    // Toggle handlers
    const toggleMaster = (checked) => {
        const newSettings = { ...settings, enabled: checked };
        saveSettings(newSettings);
    };

    const toggleChannel = (channel, checked) => {
        const newSettings = {
            ...settings,
            channels: { ...settings.channels, [channel]: checked }
        };
        saveSettings(newSettings);
    };

    const toggleEvent = (event, checked) => {
        const newSettings = {
            ...settings,
            events: { ...settings.events, [event]: checked }
        };
        saveSettings(newSettings);
    };

    const updateReminderHours = (hours) => {
        const value = parseInt(hours, 10);
        if (value > 0 && value <= 72) {
            const newSettings = { ...settings, reminder_hours: value };
            setSettings(newSettings);
            // Debounce save
            setTimeout(() => saveSettings(newSettings), 500);
        }
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="pt-6">
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const eventLabels = {
        appointment_created: { label: "Appointment Created", icon: Calendar, desc: "When a new appointment is scheduled" },
        appointment_reminder: { label: "Appointment Reminder", icon: Bell, desc: "Before upcoming appointments" },
        appointment_cancelled: { label: "Appointment Cancelled", icon: X, desc: "When an appointment is cancelled" },
        invoice_generated: { label: "Invoice Generated", icon: FileText, desc: "When a new invoice is created" },
        invoice_cancelled: { label: "Invoice Cancelled", icon: AlertCircle, desc: "When an invoice is cancelled" }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Bell className="h-5 w-5 text-primary" />
                        <div>
                            <CardTitle className="text-lg">Patient Notifications</CardTitle>
                            <CardDescription>
                                {type === "clinic"
                                    ? "Configure notifications for clinic appointments"
                                    : "Configure notifications for your patients"
                                }
                            </CardDescription>
                        </div>
                    </div>
                    <Switch
                        checked={settings.enabled}
                        onCheckedChange={toggleMaster}
                        disabled={saving}
                    />
                </div>
            </CardHeader>

            {settings.enabled && (
                <CardContent className="space-y-6">
                    {/* Channels */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                            Notification Channels
                        </h4>
                        <div className="grid gap-3">
                            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                                <div className="flex items-center gap-3">
                                    <MessageCircle className="h-5 w-5 text-green-500" />
                                    <div>
                                        <Label className="font-medium">WhatsApp</Label>
                                        <p className="text-xs text-muted-foreground">Send via WhatsApp Business</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={settings.channels?.whatsapp ?? true}
                                    onCheckedChange={(c) => toggleChannel("whatsapp", c)}
                                    disabled={saving}
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                                <div className="flex items-center gap-3">
                                    <Mail className="h-5 w-5 text-blue-500" />
                                    <div>
                                        <Label className="font-medium">Email</Label>
                                        <p className="text-xs text-muted-foreground">Send to patient email</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={settings.channels?.email ?? true}
                                    onCheckedChange={(c) => toggleChannel("email", c)}
                                    disabled={saving}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Events */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                            Notification Events
                        </h4>
                        <div className="grid gap-2">
                            {Object.entries(eventLabels).map(([key, { label, icon: Icon, desc }]) => (
                                <div
                                    key={key}
                                    className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                                >
                                    <div className="flex items-center gap-3">
                                        <Icon className="h-4 w-4 text-muted-foreground" />
                                        <div>
                                            <Label className="font-medium text-sm">{label}</Label>
                                            <p className="text-xs text-muted-foreground">{desc}</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={settings.events?.[key] ?? true}
                                        onCheckedChange={(c) => toggleEvent(key, c)}
                                        disabled={saving}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Reminder Hours */}
                    {settings.events?.appointment_reminder && (
                        <div className="space-y-3">
                            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                                Reminder Timing
                            </h4>
                            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                                <Label className="text-sm">Send reminder</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="72"
                                    value={settings.reminder_hours || 6}
                                    onChange={(e) => updateReminderHours(e.target.value)}
                                    className="w-20 h-8"
                                    disabled={saving}
                                />
                                <Label className="text-sm text-muted-foreground">hours before appointment</Label>
                            </div>
                        </div>
                    )}
                </CardContent>
            )}
        </Card>
    );
}

NotificationSettings.propTypes = {
    token: PropTypes.string,
    type: PropTypes.string,
    clinicId: PropTypes.string,
    initialSettings: PropTypes.object,
    onUpdate: PropTypes.func
};
